import { useState, useCallback } from 'react';
import api from '@/lib/api';
import { Sparkles, Check, X, Loader2, RotateCcw, Languages, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

export default function AIOptimizePanel({ product, onApply }) {
  const [activeTask, setActiveTask] = useState(null);
  const [result, setResult] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [customField, setCustomField] = useState('description');
  const [transLang, setTransLang] = useState('es');

  const handle = product?.handle;

  const optimize = useCallback(async (type) => {
    if (!handle) return;
    setActiveTask(type);
    setResult(null);
    try {
      const endpoint = type === 'title' ? 'optimize-title' : type === 'description' ? 'optimize-description' : 'optimize-all';
      const { data } = await api.post(`/ai/product/${handle}/${endpoint}`);
      setResult({ type, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI optimization failed');
    }
    setActiveTask(null);
  }, [handle]);

  const customEdit = useCallback(async () => {
    if (!handle || !customPrompt.trim()) return;
    setActiveTask('custom');
    setResult(null);
    try {
      const { data } = await api.post(`/ai/product/${handle}/custom-edit`, { field: customField, prompt: customPrompt });
      setResult({ type: 'custom', data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Custom edit failed');
    }
    setActiveTask(null);
  }, [handle, customPrompt, customField]);

  const translate = useCallback(async () => {
    if (!handle) return;
    setActiveTask('translate');
    setResult(null);
    try {
      const { data } = await api.post(`/ai/product/${handle}/translate`, { language: transLang });
      setResult({ type: 'translate', data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Translation failed');
    }
    setActiveTask(null);
  }, [handle, transLang]);

  const applyResult = useCallback(async () => {
    if (!result || !handle) return;
    try {
      const updates = {};
      if (result.type === 'title' && result.data.optimized) updates.title = result.data.optimized;
      if (result.type === 'description' && result.data.optimized) updates.description = result.data.optimized;
      if (result.type === 'all') {
        if (result.data.title?.optimized) updates.title = result.data.title.optimized;
        if (result.data.description?.optimized) updates.description = result.data.description.optimized;
      }
      if (result.type === 'custom' && result.data.edited) updates[result.data.field] = result.data.edited;
      if (Object.keys(updates).length > 0) {
        await api.post(`/ai/product/${handle}/apply-optimization`, updates);
        toast.success('Optimization applied!');
        if (onApply) onApply(updates);
      }
      setResult(null);
    } catch (e) {
      toast.error('Failed to apply');
    }
  }, [result, handle, onApply]);

  const inputStyle = { borderColor: 'rgba(26,26,26,0.12)' };

  return (
    <div data-testid="ai-optimize-panel" className="p-5 rounded-lg border" style={{ borderColor: 'rgba(196,149,106,0.3)', backgroundColor: 'rgba(196,149,106,0.03)' }}>
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={16} style={{ color: '#C4956A' }} />
        <h3 className="font-sans text-xs uppercase tracking-wider font-medium" style={{ color: '#C4956A' }}>AI Optimize</h3>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button data-testid="ai-optimize-title" variant="outline" size="sm" onClick={() => optimize('title')} disabled={!!activeTask}
          className="rounded-none font-sans text-xs gap-1 h-8" style={inputStyle}>
          {activeTask === 'title' ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Optimize Title
        </Button>
        <Button data-testid="ai-optimize-desc" variant="outline" size="sm" onClick={() => optimize('description')} disabled={!!activeTask}
          className="rounded-none font-sans text-xs gap-1 h-8" style={inputStyle}>
          {activeTask === 'description' ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Optimize Description
        </Button>
        <Button data-testid="ai-optimize-all" variant="outline" size="sm" onClick={() => optimize('all')} disabled={!!activeTask}
          className="rounded-none font-sans text-xs gap-1 h-8" style={inputStyle}>
          {activeTask === 'all' ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Optimize All
        </Button>
      </div>

      {/* Custom Edit */}
      <div className="flex gap-2 mb-4">
        <Select value={customField} onValueChange={setCustomField}>
          <SelectTrigger className="w-[110px] font-sans text-xs rounded-none h-8" style={inputStyle}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="title">Title</SelectItem>
            <SelectItem value="description">Description</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="ai-custom-prompt" value={customPrompt} onChange={e => setCustomPrompt(e.target.value)}
          placeholder="e.g. Make it sound more luxurious..." className="flex-1 font-sans text-xs rounded-none h-8" style={inputStyle}
          onKeyDown={e => e.key === 'Enter' && customEdit()} />
        <Button data-testid="ai-custom-apply" variant="outline" size="sm" onClick={customEdit} disabled={!!activeTask || !customPrompt.trim()}
          className="rounded-none font-sans text-xs gap-1 h-8" style={inputStyle}>
          {activeTask === 'custom' ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={12} />} Edit
        </Button>
      </div>

      {/* Translate */}
      <div className="flex gap-2 mb-4">
        <Select value={transLang} onValueChange={setTransLang}>
          <SelectTrigger className="w-[130px] font-sans text-xs rounded-none h-8" style={inputStyle}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="es">Spanish</SelectItem>
            <SelectItem value="fr">French</SelectItem>
            <SelectItem value="de">German</SelectItem>
            <SelectItem value="it">Italian</SelectItem>
            <SelectItem value="pt">Portuguese</SelectItem>
            <SelectItem value="ja">Japanese</SelectItem>
            <SelectItem value="zh">Chinese</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="ai-translate-btn" variant="outline" size="sm" onClick={translate} disabled={!!activeTask}
          className="rounded-none font-sans text-xs gap-1 h-8" style={inputStyle}>
          {activeTask === 'translate' ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />} Translate
        </Button>
      </div>

      {/* Result */}
      {result && (
        <div data-testid="ai-result" className="mt-4 p-4 rounded border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#fff' }}>
          {result.type === 'title' && (
            <div className="space-y-2">
              <p className="font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>Title Optimization</p>
              <p className="font-sans text-sm line-through" style={{ color: 'rgba(26,26,26,0.4)' }}>{result.data.original}</p>
              <p className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>{result.data.optimized}</p>
            </div>
          )}
          {result.type === 'description' && (
            <div className="space-y-2">
              <p className="font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>Description Optimization</p>
              <div className="font-sans text-xs max-h-40 overflow-auto p-2 rounded" style={{ backgroundColor: 'rgba(26,26,26,0.02)' }} dangerouslySetInnerHTML={{ __html: result.data.optimized }} />
            </div>
          )}
          {result.type === 'all' && (
            <div className="space-y-3">
              <p className="font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>Full Optimization</p>
              {result.data.title?.optimized && <div><p className="font-sans text-[10px] mb-1" style={{ color: 'rgba(26,26,26,0.4)' }}>Title:</p><p className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>{result.data.title.optimized}</p></div>}
              {result.data.description?.optimized && <div><p className="font-sans text-[10px] mb-1" style={{ color: 'rgba(26,26,26,0.4)' }}>Description:</p><div className="font-sans text-xs max-h-32 overflow-auto" dangerouslySetInnerHTML={{ __html: result.data.description.optimized }} /></div>}
            </div>
          )}
          {result.type === 'custom' && (
            <div className="space-y-2">
              <p className="font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>Custom Edit — {result.data.field}</p>
              {result.data.field === 'title' ? (
                <p className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>{result.data.edited}</p>
              ) : (
                <div className="font-sans text-xs max-h-40 overflow-auto" dangerouslySetInnerHTML={{ __html: result.data.edited }} />
              )}
            </div>
          )}
          {result.type === 'translate' && (
            <div className="space-y-2">
              <p className="font-sans text-[10px] uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>Translation — {result.data.language}</p>
              <p className="font-sans text-sm font-medium">{result.data.translated_title}</p>
              <div className="font-sans text-xs max-h-32 overflow-auto" dangerouslySetInnerHTML={{ __html: result.data.translated_description }} />
            </div>
          )}

          {/* Accept / Reject */}
          {result.type !== 'translate' && (
            <div className="flex gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
              <Button data-testid="ai-accept" size="sm" onClick={applyResult} className="rounded-none font-sans text-xs gap-1 h-8" style={{ backgroundColor: '#16a34a', color: '#fff' }}>
                <Check size={12} /> Accept
              </Button>
              <Button data-testid="ai-reject" variant="outline" size="sm" onClick={() => setResult(null)} className="rounded-none font-sans text-xs gap-1 h-8" style={inputStyle}>
                <X size={12} /> Reject
              </Button>
              <Button variant="outline" size="sm" onClick={() => optimize(result.type)} className="rounded-none font-sans text-xs gap-1 h-8" style={inputStyle}>
                <RotateCcw size={12} /> Regenerate
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
